import React, { useEffect, useLayoutEffect, useId, useImperativeHandle, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { Alert, Col, Form, ListGroup, Offcanvas, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FieldArray, FieldArrayRenderProps, useField, useFormikContext } from 'formik'

import { TagEditor } from '..'
import { modifyTemplateArticle, deleteFigure } from '../articleAPI'
import { stripTags } from '../text'
import { useFormikReset, DirtyEffect } from '../CommonComponents'
import { ArticleNameTitle } from '../ArticleNameTitle'
import { Article, Figure, Layoutable, TemplateArticle } from '../types'

const NULLISH_LOCATION = '__SSC_TEMPLATE_ARTICLES_DETAIL_NULLISH_LOCATION__'

type FormTemplateArticle = TemplateArticle & {
  location: string
}

type SubmitTemplateArticle = TemplateArticle & {
  location: string | null
  deleteFile?: boolean
}

interface FormValues {
  templateArticles: FormTemplateArticle[]
}

type EventHandler = {
  onSubmit: (values: FormValues) => Promise<unknown>
}

const isDirtyTextFrame = (frame: FormTemplateArticle, initialFrame: FormTemplateArticle): boolean => {
  return frame.taggedContent !== initialFrame.taggedContent
}

const isDirtyFigureFrame = (frame: FormTemplateArticle, initialFrame: FormTemplateArticle): boolean => {
  return frame.location !== initialFrame.location
}

const isDirtyFrame = (frame: FormTemplateArticle, initialFrame: FormTemplateArticle): boolean => {
  if (frame.templateArticleType === 'text' && initialFrame.templateArticleType === 'text') {
    return isDirtyTextFrame(frame, initialFrame)
  } else if (frame.templateArticleType === 'figure' && initialFrame.templateArticleType === 'figure') {
    return isDirtyFigureFrame(frame, initialFrame)
  }

  throw new Error('something wrong')
}

const NoTemplateArticle = () => {
  return <>在版記事なし</>
}

interface TextFieldProps {
  index: number
  layoutable: Layoutable
  inputDisabled: boolean
}

const TextField: React.FC<TextFieldProps> = ({ index, layoutable, inputDisabled }) => {
  const [, , contentHelper] = useField<string | undefined>(`templateArticles.${index}.content`)
  const [taggedField, taggedMeta, taggedHelper] = useField<string | undefined>(
    `templateArticles.${index}.taggedContent`
  )

  const { resetCount } = useFormikReset()

  const onTagEditorChange = (taggedContent: string, _old: string | undefined) => {
    const content = stripTags(taggedContent)
    contentHelper.setValue(content ?? undefined)
    taggedHelper.setValue(taggedContent)
  }

  return (
    <>
      <hr />
      <Row>
        <Col sm='auto'>{index + 1}</Col>
        <Col>
          <DirtyEffect dirty={taggedField.value !== taggedMeta.initialValue}>
            <TagEditor
              key={resetCount}
              initialTaggedContent={taggedField.value}
              setting={layoutable.setting}
              styleTagType='sentence'
              onSubmit={(_e) => undefined}
              disabled={inputDisabled}
              editable={!inputDisabled}
              onChange={onTagEditorChange}
              allowLineBreak
              allowMultiParagraph
              appearance='withoutSubmit'
            />
          </DirtyEffect>
        </Col>
      </Row>
    </>
  )
}

interface FigureDeleteFieldProps {
  index: number
  inputDisabled: boolean
}

const FigureDeleteField: React.FC<FigureDeleteFieldProps> = ({ index, inputDisabled }) => {
  const checkId = useId()
  const [locationField, locationMeta, locationHelper] = useField<string>(`templateArticles.${index}.location`)

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    locationHelper.setValue(e.target.checked ? NULLISH_LOCATION : (locationMeta.initialValue ?? NULLISH_LOCATION))
  }

  return (
    <DirtyEffect dirty={locationField.value !== locationMeta.initialValue}>
      <div className='m-1'>画像ファイルは割り当てられています</div>
      <Alert variant='danger'>
        <Form.Check
          id={checkId}
          type='switch'
          label={
            <>
              <FontAwesomeIcon icon='trash' />
              画像ファイルを削除する
            </>
          }
          checked={locationField.value === NULLISH_LOCATION}
          onChange={onChange}
          disabled={inputDisabled}
        />
      </Alert>
    </DirtyEffect>
  )
}

interface FigureRequiredFieldProps {
  index: number
}

const FigureRequiredField: React.FC<FigureRequiredFieldProps> = ({ index }) => {
  const [filenameField] = useField<string | undefined>(`templateArticles.${index}.filename`)

  return (
    <DirtyEffect dirty={false}>
      <Alert variant='danger'>
        <div className='m-1'>画像ファイルが紙面にありません</div>
        <div className='m-1'>
          「{filenameField.value}
          」を「複数記事追加」からアップロードしてください。
        </div>
      </Alert>
    </DirtyEffect>
  )
}

interface FigureAssignFieldProps {
  index: number
  inputDisabled: boolean
  targetFigure: Figure
}

const FigureAssignField: React.FC<FigureAssignFieldProps> = ({ index, inputDisabled, targetFigure }) => {
  const checkId = useId()
  const [locationField, locationMeta, locationHelper] = useField<string>(`templateArticles.${index}.location`)

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    locationHelper.setValue(e.target.checked ? (targetFigure.location ?? NULLISH_LOCATION) : NULLISH_LOCATION, false)
  }

  return (
    <DirtyEffect dirty={locationField.value !== locationMeta.initialValue}>
      <div className='m-1'>画像ファイルが割り当てられていません</div>
      <Form.Check
        id={checkId}
        type='switch'
        label='画像ファイルを割り当てる'
        checked={locationField.value !== NULLISH_LOCATION}
        onChange={onChange}
        disabled={inputDisabled}
      />
    </DirtyEffect>
  )
}

interface FigureFieldProps {
  holderArticle?: Article
  index: number
  inputDisabled: boolean
  layoutable: Layoutable
}

const FigureField: React.FC<FigureFieldProps> = ({ holderArticle, index, inputDisabled }) => {
  const [templateField, templateMeta] = useField<FormTemplateArticle>(`templateArticles.${index}`)
  const targetFigure = holderArticle?.figures?.find((f) => f.filename === templateField.value.filename)

  const hadLocation = (templateMeta.initialValue?.location ?? NULLISH_LOCATION) !== NULLISH_LOCATION

  const field = hadLocation ? (
    <FigureDeleteField index={index} inputDisabled={inputDisabled} />
  ) : targetFigure ? (
    <FigureAssignField index={index} inputDisabled={inputDisabled} targetFigure={targetFigure} />
  ) : (
    <FigureRequiredField index={index} />
  )

  return (
    <>
      <hr />
      <Row>
        <Col sm='auto'>{index + 1}</Col>
        <Col>
          ファイル名: {templateField.value.filename}
          {field}
        </Col>
      </Row>
    </>
  )
}

interface TemplateArticleFieldProps {
  index: number
  holderArticle?: Article
  inputDisabled: boolean
  layoutable: Layoutable
}

const TemplateArticleField: React.FC<TemplateArticleFieldProps> = ({
  index,
  holderArticle,
  inputDisabled,
  layoutable
}) => {
  const [typeField] = useField<string>(`templateArticles.${index}.templateArticleType`)

  const field =
    typeField.value === 'text' ? (
      <TextField index={index} layoutable={layoutable} inputDisabled={inputDisabled} />
    ) : typeField.value === 'figure' ? (
      <FigureField index={index} holderArticle={holderArticle} inputDisabled={inputDisabled} layoutable={layoutable} />
    ) : (
      <></>
    )

  return <>{field}</>
}

interface TemplateArticleFieldsProps {
  article: Article
  childrenArticles: Record<string, Article[]>
  holderArticle?: Article
  inputDisabled: boolean
  layoutable: Layoutable
  arrayHelpers: FieldArrayRenderProps
}

const TemplateArticleFields: React.FC<TemplateArticleFieldsProps> = ({ holderArticle, inputDisabled, layoutable }) => {
  const [templateArticlesField] = useField<FormTemplateArticle[]>('templateArticles')

  const templateArticleFields = templateArticlesField.value.map((template, index) => {
    return (
      <TemplateArticleField
        key={template.id ?? index}
        index={index}
        holderArticle={holderArticle}
        inputDisabled={inputDisabled}
        layoutable={layoutable}
      />
    )
  })

  return <ListGroup>{templateArticleFields}</ListGroup>
}

interface ArticleTemplateArticlesDetailBodyProps {
  article: Article
  childrenArticles: Record<string, Article[]>
  holderArticle?: Article
  inputDisabled: boolean
  layoutable: Layoutable
}

const ArticleTemplateArticlesDetailBody: React.FC<ArticleTemplateArticlesDetailBodyProps> = (props) => {
  const [templateArticlesField] = useField<FormTemplateArticle[]>('templateArticles')

  if (!templateArticlesField.value?.length) {
    return <NoTemplateArticle />
  }

  return (
    <>
      <div>フレーム数: {templateArticlesField.value?.length}</div>
      <FieldArray name='templateArticles'>
        {(arrayHelpers) => <TemplateArticleFields arrayHelpers={arrayHelpers} {...props} />}
      </FieldArray>
    </>
  )
}

const submitUpdates = async (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  frames: SubmitTemplateArticle[],
  initialFrames: FormTemplateArticle[],
  holderArticle: Article | undefined,
  layoutable: Layoutable,
  layoutableType: string,
  layoutableId: string | number
): Promise<PromiseSettledResult<unknown>[]> => {
  const promises = frames.flatMap((frame, index) => {
    const initialFrame = initialFrames[index]
    const hadLocation = initialFrame.location !== NULLISH_LOCATION

    if (!isDirtyFrame(frame as FormTemplateArticle, initialFrame)) {
      return []
    }

    if (frame.templateArticleType === 'text') {
      return dispatch(
        modifyTemplateArticle({
          layoutableType,
          layoutableId,
          lockVersion: layoutable.lockVersion,
          templateArticle: frame
        } as Parameters<typeof modifyTemplateArticle>[0])
      )
    } else if (frame.templateArticleType === 'figure') {
      if (hadLocation && frame.location === null) {
        return dispatch(
          modifyTemplateArticle({
            layoutableType,
            layoutableId,
            lockVersion: layoutable.lockVersion,
            templateArticle: { ...frame, deleteFile: true }
          } as Parameters<typeof modifyTemplateArticle>[0])
        )
      }

      if (!hadLocation && frame.location !== null) {
        const matchingHolderFigure = holderArticle?.figures?.find((f) => f.filename === frame.filename)
        const delFigure = {
          id: matchingHolderFigure!.id!,
          keepFile: true
        }
        console.log('assign template figure', { delFigure, frame })
        return [
          dispatch(
            modifyTemplateArticle({
              layoutableType,
              layoutableId,
              lockVersion: layoutable.lockVersion,
              templateArticle: frame
            } as Parameters<typeof modifyTemplateArticle>[0])
          ),
          dispatch(
            deleteFigure({
              layoutableType,
              layoutableId,
              figure: delFigure
            } as Parameters<typeof deleteFigure>[0])
          )
        ]
      }
    }

    return []
  })

  return Promise.allSettled(promises)
}

interface Props {
  article: Article
  childrenArticles: Record<string, Article[]>
  eventHandlerRef: React.RefObject<EventHandler | null>
  holderArticle?: Article
  inputDisabled: boolean
  layoutable: Layoutable
  layoutableId: string | number
  layoutableType: string
}

export const ArticleTemplateArticlesDetail: React.FC<Props> = ({
  article,
  childrenArticles,
  eventHandlerRef,
  holderArticle,
  inputDisabled,
  layoutable,
  layoutableId,
  layoutableType
}) => {
  const commonProps: ArticleTemplateArticlesDetailBodyProps = {
    article,
    childrenArticles,
    holderArticle,
    inputDisabled,
    layoutable
  }

  const initialValues: FormValues = {
    templateArticles: (article.templateArticles ?? []).map((frame) => ({
      ...frame,
      taggedContent: frame.taggedContent && frame.taggedContent.replace(/\r?\n|\r/g, ''),
      location: frame.location == null ? NULLISH_LOCATION : frame.location
    }))
  }

  const context = useFormikContext<Partial<FormValues>>()
  const formikReset = useFormikReset()
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()

  useEffect(() => {
    context.resetForm({ values: initialValues })

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [])

  const focusElementRef = useRef<HTMLSpanElement>(null)

  useImperativeHandle(eventHandlerRef, () => ({
    onSubmit: async (_values: FormValues) => {
      const templateArticles = (context.values.templateArticles ?? []).map((frame) => ({
        ...frame,
        location: frame.location === NULLISH_LOCATION ? null : frame.location
      }))
      return submitUpdates(
        dispatch,
        templateArticles,
        context.initialValues.templateArticles ?? [],
        holderArticle,
        layoutable,
        layoutableType,
        layoutableId
      ).then(async (results) => {
        const outcomes = results.map(
          (r) => (r as PromiseFulfilledResult<{ success?: boolean; index?: number; error?: unknown }>).value
        )
        const failures = outcomes.filter((o) => !o?.success)

        failures.forEach(({ index, error }) => {
          console.error(`submit failed: ${index}, ${error}`)
        })

        if (failures.length > 0) {
          throw new Error('submit template update failed')
        }

        formikReset.resetForm({ values: context.values })
      })
    }
  }))

  useLayoutEffect(() => {
    const timeoutId = setTimeout(() => {
      focusElementRef.current?.focus()
      focusElementRef.current?.blur()
    }, 2)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [context.initialValues])

  if ((context.values as Partial<FormValues> | undefined)?.templateArticles === undefined) {
    return undefined
  }

  return (
    <>
      <Offcanvas.Header>
        <Offcanvas.Title className='detail-offcanvas-title'>
          在版
          <ArticleNameTitle article={article} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <div
        className='flex-grow-1 overflow-auto m-1'
        style={{
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: 'var(--bs-border-color)'
        }}
      >
        <Offcanvas.Body className='article-template-articles-detail-offcanvas-body'>
          <span
            ref={focusElementRef}
            tabIndex={-1}
            aria-hidden='true'
            style={{
              opacity: 0,
              height: 0,
              width: 0,
              top: 0,
              left: 0
            }}
          />
          <ArticleTemplateArticlesDetailBody {...commonProps} />
        </Offcanvas.Body>
      </div>
    </>
  )
}
